import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { LayoutDashboard, Truck, Container, Disc3, TrendingUp, LogOut, Bell, BookOpen, Users, KeyRound, BarChart3, Wrench, Package, ClipboardCheck } from "lucide-react";
import { cn } from "../lib/utils";
import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [alertCount, setAlertCount] = useState(0);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    api.get("/alerts").then((r) => {
      setAlertCount((r.data.life_limit?.length || 0) + (r.data.rotation_due?.length || 0));
    }).catch(() => {});
  }, []);
  useAutoRefresh(() => {
    api.get("/alerts").then((r) => {
      setAlertCount((r.data.life_limit?.length || 0) + (r.data.rotation_due?.length || 0));
    }).catch(() => {});
  }, 30000);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password berhasil diubah");
      setPwOpen(false);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwBusy(false);
    }
  };

  const isManagement = user?.role === "management" || user?.role === "admin";

  const navItems = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/trucks", label: "Truck", icon: Truck, testId: "nav-trucks" },
    { to: "/trailers", label: "Trailer", icon: Container, testId: "nav-trailers" },
    { to: "/tyres", label: "Ban", icon: Disc3, testId: "nav-tyres" },
    { to: "/alerts", label: "Alert", icon: Bell, testId: "nav-alerts", badge: alertCount },
    { to: "/logbook", label: "Logbook", icon: ClipboardCheck, testId: "nav-logbook" },
    { to: "/sparepart-inventory", label: "Stok Sparepart", icon: Package, testId: "nav-sparepart-inventory" },
  ];
  if (isManagement) {
    navItems.push({ to: "/tyre-catalog", label: "Master Harga", icon: BookOpen, testId: "nav-catalog" });
    navItems.push({ to: "/sparepart-master", label: "Master Sparepart", icon: Wrench, testId: "nav-sparepart-master" });
    navItems.push({ to: "/users", label: "Akun", icon: Users, testId: "nav-users" });
    navItems.push({ to: "/reports/vehicle", label: "Per Unit", icon: BarChart3, testId: "nav-vehicle-report" });
    navItems.push({ to: "/reports/cost", label: "Laporan Biaya", icon: TrendingUp, testId: "nav-cost" });
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-slate-900 border-b-2 border-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <img src="/logo-rpi.jpg" alt="Royal Persada Indah" className="w-9 h-9 object-contain bg-white rounded-sm p-1" />
            <div className="hidden sm:block">
              <div className="text-[9px] uppercase tracking-widest font-bold text-orange-400 leading-none">PT · Royal Persada Indah</div>
              <div className="font-display font-black text-white text-base tracking-tight leading-tight">
                Fleet<span className="text-orange-500">Monitor</span>
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testId}
                className={({ isActive }) =>
                  cn(
                    "px-3 py-2 text-sm font-semibold uppercase tracking-wider flex items-center gap-2 border-b-2",
                    isActive
                      ? "text-orange-400 border-orange-500"
                      : "text-slate-300 border-transparent hover:text-white"
                  )
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
                {item.badge > 0 && (
                  <span className="ml-1 bg-red-600 text-white text-[10px] font-mono font-bold px-1.5 rounded-sm">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end text-xs">
              <span className="text-white font-semibold" data-testid="user-name">{user?.name}</span>
              <span className="text-orange-400 uppercase tracking-wider text-[10px]" data-testid="user-role">{user?.role}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPwOpen(true)}
              className="text-slate-300 hover:text-white hover:bg-slate-800"
              data-testid="change-password-btn"
              title="Ubah Password"
            >
              <KeyRound className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-300 hover:text-white hover:bg-slate-800"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Mobile nav */}
        <nav className="md:hidden flex overflow-x-auto border-t border-slate-800">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                cn(
                  "flex-shrink-0 px-4 py-2 text-xs font-semibold uppercase flex items-center gap-1",
                  isActive ? "text-orange-400" : "text-slate-400"
                )
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 animate-fade-in-up">
        <Outlet />
      </main>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">Ubah Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitPassword} className="space-y-4">
            <div>
              <Label className="uppercase text-xs tracking-widest font-bold">Password Saat Ini *</Label>
              <Input
                data-testid="current-password-input"
                required
                type="password"
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                className="h-12 rounded-sm font-mono"
              />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-widest font-bold">Password Baru * (min 6 karakter)</Label>
              <Input
                data-testid="new-password-input"
                required
                type="password"
                minLength={6}
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                className="h-12 rounded-sm font-mono"
              />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-widest font-bold">Konfirmasi Password Baru *</Label>
              <Input
                data-testid="confirm-password-input"
                required
                type="password"
                minLength={6}
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                className="h-12 rounded-sm font-mono"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPwOpen(false)} className="rounded-sm">Batal</Button>
              <Button
                type="submit"
                disabled={pwBusy}
                data-testid="change-password-submit"
                className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial"
              >
                {pwBusy ? "..." : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
